import React, { useRef } from 'react';
import { formatCurrency, formatDate } from '../utils/formatters';

const InvoicePreview = ({ invoice, onBack }) => {
  const printRef = useRef();

  const handlePrint = () => {
    const printContent = printRef.current;
    const originalContents = document.body.innerHTML;

    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'DRAFT',
      issued: 'ISSUED',
      partial: 'PARTIALLY PAID',
      paid: 'PAID',
      overdue: 'OVERDUE',
      cancelled: 'CANCELLED'
    };
    return labels[status] || status.toUpperCase();
  };

  return (
    <div>
      {/* Action Buttons (not printed) */}
      <div className="flex gap-3 mb-6 print:hidden">
        <button
          onClick={onBack}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
        >
          Back to Details
        </button>
        <button
          onClick={handlePrint}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Print Invoice
        </button>
      </div>

      {/* Printable Invoice */}
      <div ref={printRef} className="bg-white p-8 max-w-4xl mx-auto">
        {/* Styles for printing */}
        <style>
          {`
            @media print {
              body {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              .print-status-badge {
                padding: 4px 12px;
                border-radius: 4px;
                font-weight: bold;
                display: inline-block;
              }
              .status-paid { background-color: #d1fae5; color: #065f46; }
              .status-partial { background-color: #dbeafe; color: #1e40af; }
              .status-issued { background-color: #fef3c7; color: #92400e; }
              .status-overdue { background-color: #fee2e2; color: #991b1b; }
              .status-draft { background-color: #f3f4f6; color: #374151; }
              .status-cancelled { background-color: #f3f4f6; color: #6b7280; }
            }
          `}
        </style>

        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">INVOICE</h1>
            <p className="text-lg text-gray-600 mt-1">{invoice.invoiceNumber}</p>
          </div>
          <div className="text-right">
            <div className={`print-status-badge status-${invoice.status} px-3 py-1 rounded text-sm font-bold
              ${invoice.status === 'paid' ? 'bg-green-100 text-green-800' : ''}
              ${invoice.status === 'partial' ? 'bg-blue-100 text-blue-800' : ''}
              ${invoice.status === 'issued' ? 'bg-yellow-100 text-yellow-800' : ''}
              ${invoice.status === 'overdue' ? 'bg-red-100 text-red-800' : ''}
              ${invoice.status === 'draft' ? 'bg-gray-100 text-gray-600' : ''}
              ${invoice.status === 'cancelled' ? 'bg-gray-100 text-gray-500' : ''}
            `}>
              {getStatusLabel(invoice.status)}
            </div>
          </div>
        </div>

        {/* From / To */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase mb-2">From</h3>
            <div className="text-gray-900 font-semibold">Your Company Name</div>
            <div className="text-gray-600 text-sm">
              Your Address<br />
              Your City, Country<br />
              Phone: +233 XXX XXX XXX
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase mb-2">Bill To</h3>
            <div className="text-gray-900 font-semibold">{invoice.customerName}</div>
            <div className="text-gray-600 text-sm">
              {invoice.customerPhone}<br />
              {invoice.customerLocation && <>{invoice.customerLocation}<br /></>}
            </div>
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-3 gap-4 mb-8 bg-gray-50 p-4 rounded-lg">
          <div>
            <span className="text-sm text-gray-500">Invoice Date</span>
            <div className="font-medium">{invoice.issueDate ? formatDate(invoice.issueDate) : '-'}</div>
          </div>
          <div>
            <span className="text-sm text-gray-500">Due Date</span>
            <div className={`font-medium ${invoice.status === 'overdue' ? 'text-red-600' : ''}`}>
              {invoice.dueDate ? formatDate(invoice.dueDate) : '-'}
            </div>
          </div>
          <div>
            <span className="text-sm text-gray-500">Balance Due</span>
            <div className={`font-bold text-lg ${invoice.balance > 0 ? 'text-orange-600' : 'text-green-600'}`}>
              {formatCurrency(invoice.balance)}
            </div>
          </div>
        </div>

        {/* Line Items Table */}
        <table className="w-full mb-8">
          <thead>
            <tr className="border-b-2 border-gray-300">
              <th className="text-left py-3 text-sm font-semibold text-gray-600">Description</th>
              <th className="text-right py-3 text-sm font-semibold text-gray-600 w-20">Qty</th>
              <th className="text-right py-3 text-sm font-semibold text-gray-600 w-32">Unit Price</th>
              <th className="text-right py-3 text-sm font-semibold text-gray-600 w-32">Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lineItems?.map((item, index) => (
              <tr key={index} className="border-b border-gray-200">
                <td className="py-3">
                  <div className="font-medium text-gray-900">{item.productName}</div>
                  {item.description && (
                    <div className="text-sm text-gray-500">{item.description}</div>
                  )}
                </td>
                <td className="py-3 text-right text-gray-900">{item.quantity}</td>
                <td className="py-3 text-right text-gray-900">{formatCurrency(item.unitPrice)}</td>
                <td className="py-3 text-right font-medium text-gray-900">{formatCurrency(item.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end mb-8">
          <div className="w-64">
            <div className="flex justify-between py-2 border-b border-gray-200">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-medium">{formatCurrency(invoice.subtotal)}</span>
            </div>
            {invoice.taxRate > 0 && (
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Tax ({invoice.taxRate}%)</span>
                <span className="font-medium">{formatCurrency(invoice.taxAmount)}</span>
              </div>
            )}
            <div className="flex justify-between py-2 border-b-2 border-gray-300">
              <span className="font-semibold">Total</span>
              <span className="font-bold">{formatCurrency(invoice.totalAmount)}</span>
            </div>
            {invoice.amountPaid > 0 && (
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Amount Paid</span>
                <span className="font-medium text-green-600">-{formatCurrency(invoice.amountPaid)}</span>
              </div>
            )}
            <div className="flex justify-between py-3 bg-gray-50 px-2 rounded mt-2">
              <span className="font-bold">Amount Due</span>
              <span className={`font-bold text-lg ${invoice.balance > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                {formatCurrency(invoice.balance)}
              </span>
            </div>
          </div>
        </div>

        {/* Payment History (if any) */}
        {invoice.payments && invoice.payments.length > 0 && (
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-gray-600 uppercase mb-3">Payment History</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 text-gray-500">Date</th>
                  <th className="text-left py-2 text-gray-500">Method</th>
                  <th className="text-left py-2 text-gray-500">Reference</th>
                  <th className="text-right py-2 text-gray-500">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.payments.map((payment) => (
                  <tr key={payment.id} className="border-b border-gray-100">
                    <td className="py-2">{formatDate(payment.paymentDate)}</td>
                    <td className="py-2 capitalize">
                      {payment.paymentMethod?.replace('_', ' ')}
                      {payment.mobileMoneyProvider && ` (${payment.mobileMoneyProvider})`}
                    </td>
                    <td className="py-2 text-gray-500">{payment.transactionReference || '-'}</td>
                    <td className="py-2 text-right font-medium text-green-600">{formatCurrency(payment.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Notes & Terms */}
        <div className="grid grid-cols-2 gap-8 pt-6 border-t border-gray-200">
          {invoice.notes && (
            <div>
              <h3 className="text-sm font-semibold text-gray-600 uppercase mb-2">Notes</h3>
              <p className="text-sm text-gray-700">{invoice.notes}</p>
            </div>
          )}
          {invoice.terms && (
            <div>
              <h3 className="text-sm font-semibold text-gray-600 uppercase mb-2">Terms & Conditions</h3>
              <p className="text-sm text-gray-700">{invoice.terms}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
          <p>Thank you for your business!</p>
        </div>
      </div>
    </div>
  );
};

export default InvoicePreview;
